import React, { FC, useMemo, ReactElement, useCallback } from 'react';
import { connect } from 'react-redux';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { Button, Card, Col, Layout, Row, Table } from 'antd';
import { HorizontalKeyValueDisplay } from '../horizontal_key_value_display/HorizontalKeyValueDisplay';
import { withRouter } from 'react-router';
import ReservedQtyDisplay from './ReservedQtyDisplay';
import RequestedQuantityDisplay from './RequestedQuantityDisplay';
import { DATE_FORMAT, dateFormatter, getContactName } from '../../utils/DataFormatterUtils';
import { ExportOutlined } from '@ant-design/icons';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';

const { Content } = Layout;
const BACK_TEXT = 'Back to Part Per Stock Location Detail';

const getFieldName = (fieldKey) => {
	const allNames = {
		maxQuantity: 'Max Quantity',
		minQuantity: 'Min Quantity',
		onHandQuantity: 'On Hand Quantity',
		transferred: 'Transferred',
		manual: 'Manual',
		purchase_order: 'Purchase Order',
	};
	return allNames[fieldKey] || fieldKey;
};

const PartsPerStockLocationDetailsDetailPage: FC<any> = ({
	ppsl,
	fetching,
	stockLocation,
}): ReactElement => {
	const reservedQuantityAvailable = useMemo(() => false, []);

	const epslDetail = useMemo(
		() => ({
			'Available Qty': nullSafeGetOrElse('availableQuantity', ppsl, 0),
			'On Hand Qty': nullSafeGetOrElse('onHandQuantity', ppsl, 0),
			...(reservedQuantityAvailable && {
				'Reserved Qty': <ReservedQtyDisplay record={ppsl} backText={BACK_TEXT} />,
			}),
			'Min Qty': nullSafeGetOrElse('minQuantity', ppsl, 0),
			'Max Qty': nullSafeGetOrElse('maxQuantity', ppsl, 0),
			'Requested Qty': <RequestedQuantityDisplay record={ppsl} backText={BACK_TEXT} />,
			'Stock Location': nullSafeGetOrElse('stockLocation.name', ppsl, '--'),
			Aisle: nullSafeGetOrElse('aisle', ppsl, '--'),
			Bay: nullSafeGetOrElse('bay', ppsl, '--'),
			Level: nullSafeGetOrElse('level', ppsl, '--'),
			Bin: nullSafeGetOrElse('bin', ppsl, '--'),
		}),
		[ppsl, reservedQuantityAvailable]
	);

	const auditColumns = useMemo(
		() => [
			{
				title: 'Updated At',
				dataIndex: 'updatedAt',
				render: (_) => (_ ? dateFormatter(_, DATE_FORMAT) : '--'),
			},
			{
				title: 'Update Type',
				dataIndex: 'updateType',
				render: (_) => getFieldName(_ || 'manual'),
			},
			{
				title: 'Updated By',
				dataIndex: 'updatedByContact',
				render: (_) => getContactName(_),
			},
			{
				title: 'Updated Fields',
				dataIndex: 'fieldValueChanges',
				render: (fields) =>
					fields.map((field, idx) => {
						return (
							<div key={`${idx}-changed-fields`}>
								{`${getFieldName(field.fieldName)} from ${nullSafeGetOrElse(
									'oldValue',
									field,
									'--'
								)} to ${nullSafeGetOrElse('newValue', field, '--')}`}
							</div>
						);
					}),
			},
		],
		[]
	);

	const csvTitle = useMemo(
		() =>
			`${nullSafeGetOrElse('part.name', ppsl, '')}_at_${nullSafeGetOrElse(
				'name',
				stockLocation,
				'Stock Location'
			)}_audits`,
		[ppsl, stockLocation]
	);

	const onExportHistory = useCallback(() => {
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: csvTitle,
			title: csvTitle,
			headers: auditColumns.map((_) => _.title).filter((_) => !!_),
		});
		const exportData = nullSafeGetOrElse('adjustments', ppsl, []).map((_) => ({
			updatedAt: _.updatedAt ? dateFormatter(_.updatedAt, DATE_FORMAT) : '--',
			updateType: getFieldName(_.updateType || 'manual'),
			updatedByContact: getContactName(_.updatedByContact),
			fieldValueChanges: nullSafeGetOrElse('fieldValueChanges', _, []).reduce(
				(acc, field, idx) =>
					acc +
					`\n${getFieldName(field.fieldName)} from ${nullSafeGetOrElse(
						'oldValue',
						field,
						'--'
					)} to ${nullSafeGetOrElse('newValue', field, '--')}`,
				''
			),
		}));
		csvExporter.generateCsv(exportData);
	}, [auditColumns, csvTitle, ppsl]);

	return (
		<Content
			style={{
				padding: '0 0.5em',
			}}
		>
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				<Col span={24}>
					<Card loading={fetching} title="Details">
						<HorizontalKeyValueDisplay rowStyle={{ minWidth: '25%' }} keyValueStore={epslDetail} />
					</Card>
				</Col>
			</Row>
			{!fetching && ppsl.adjustments && ppsl.adjustments.length > 0 && (
				<Row style={{ margin: '16px 4px' }} gutter={12}>
					<Col span={24}>
						<div className="rowSpacing">
							<Card
								bodyStyle={{ padding: 0 }}
								title="Audits"
								extra={
									<Button
										icon={<ExportOutlined translate="" />}
										type="ghost"
										onClick={onExportHistory}
									>
										Export
									</Button>
								}
							>
								<Table
									columns={auditColumns}
									showHeader
									rowKey={(el) => `audit-${el.idx}`}
									dataSource={ppsl.adjustments.map((_, idx) => ({ ..._, idx }))}
								/>
							</Card>
						</div>
					</Col>
				</Row>
			)}
		</Content>
	);
};

const mapStateToProps = (state) => ({
	fetching: state.parts_per_stock_locations.fetching,
	ppsl: state.parts_per_stock_locations.detail,
	stockLocation: state.stock_locations.detail,
	currentUser: state.session.currentUser,
});

const ComponentWithoutUserType = withRouter<any, any>(
	connect(mapStateToProps)(PartsPerStockLocationDetailsDetailPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
